import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuraxFlags as useFx2910 } from '../../lib/auraxFlags'; // CRIAR_NOVO_2910 / EXTRAS_2910
import { hidePath2810 } from '../../lib/semApps2810'; // EXTRAS_FIX_2910
import { resolveUserDisplayName, resolveUserSecondaryIdentity } from '../../lib/userDisplayName';
import {
  Sun,
  Moon,
  Maximize,
  Minimize,
  KeyRound,
  Menu,
  Palette,
  UserCog,
  Settings,
  LogOut,
  Zap,
  UserPlus,
  ChevronDown,
  Plus,
  LayoutDashboard,
  Search,
  MoreHorizontal,
  Download,
  CalendarPlus,
  Users,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { cn } from '../../lib/utils';
import { mergeDashboardCards } from '../../dashboardCardPolicy';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ProfileModal, ConfiguracoesModal, UserAvatar } from './AccountModals';
import OfficeMobileHeaderSheet from './OfficeMobileHeaderSheet';
import SaldoHeader2810 from './SaldoHeader2810'; // CREDITOS_HEADER_2810
import './SaldoHeader2810.css';
import { HeaderNotifyBell } from '../chats/ChatNotifyPrefs';
import { clearSessionStarted } from '../../lib/sessionTimer';
import { useOfficeSessionTimer } from '../../hooks/useOfficeSessionTimer';
import { canOperate } from '../../lib/operationalPermissions';
import { isStandalonePwa, openPwaInstallPrompt } from '../../lib/pwaInstall';
import { WhatsAppIcon, TelegramIcon, YouTubeIcon } from '../ServiceBrandIcon';

function HeaderMenuIcon({ icon: Icon, tone = 'blue' }) {
  return <span className={`office-header-menu-icon office-header-tone--${tone}`} aria-hidden="true"><Icon size={16} strokeWidth={1.8} /></span>;
}

const IconBtn = React.forwardRef(({ children, active, onClick, title, className = '', ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    className={cn(
      'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors',
      active
        ? 'bg-primary/10 text-primary'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      className,
    )}
    {...rest}
  >
    {children}
  </button>
));

export default function OfficeHeader({ title, pageContext = {}, onMenu, desktopSidebar = false, bottomNavigation = false, menuExpanded = false, unreadCount = 0, onLogout, settings, accountSummary, modules = [], onOpenCommandPalette, modernVisual = false }) {
  const { theme, accent, setAccent, setPreference, persistUserAppearance } = useTheme();
  const { user, logout, isAdmin, isAttendant } = useAuth();
  const { t } = useLanguage();
  const menuLabel = menuExpanded ? 'Fechar menu' : 'Abrir menu';
  const navigate = useNavigate();
  const fx2910 = useFx2910();
  const displayName = resolveUserDisplayName(user);
  const accountIdentity = resolveUserSecondaryIdentity(user, displayName);
  const PageIcon = pageContext.icon || LayoutDashboard;
  const pageIdentity = <div className="office-page-identity" data-tone={pageContext.tone || 'blue'}>
    <span className="office-page-icon" aria-hidden="true"><PageIcon size={18} strokeWidth={1.8} /></span>
    <div><p className="office-page-section">{pageContext.section || settings?.brand_name || 'Office'}</p><h1 title={title}>{title}</h1></div>
  </div>;
  const [perfilOpen, setPerfilOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const accountPending = useRef(null);
  const dialogReturnFocus = useRef(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [fullscreenAvailable, setFullscreenAvailable] = useState(false);
  const createPending = useRef(null);
  const shortcutPending = useRef(null);
  const afterMenuClose = (event, pending) => {
    if (!pending.current) return;
    event.preventDefault();
    const action = pending.current; pending.current = null; action();
  };

  const openHeaderDialog = (setOpen, triggerId) => {
    dialogReturnFocus.current = document.querySelector(`[data-testid="${triggerId}"]`);
    setOpen(true);
  };
  const changeHeaderDialog = (setOpen, open) => {
    setOpen(open);
    if (!open) requestAnimationFrame(() => dialogReturnFocus.current?.focus());
  };

  useEffect(() => {
    const syncFullscreen = () => {
      setFullscreen(Boolean(document.fullscreenElement));
      setFullscreenAvailable(Boolean(document.fullscreenEnabled && document.documentElement.requestFullscreen && document.exitFullscreen));
    };
    syncFullscreen();
    document.addEventListener('fullscreenchange', syncFullscreen);
    return () => document.removeEventListener('fullscreenchange', syncFullscreen);
  }, []);

  const dashCards = useMemo(
    () => mergeDashboardCards(accountSummary?.dashboard_cards),
    [accountSummary?.dashboard_cards],
  );
  const showQuickTest = (isAdmin || isAttendant || dashCards.quickTest !== false) && canOperate(user, 'clients.create');
  const hasPage = path => isAdmin || modules.some(group => (group.items || []).some(item => item.path === path));

  const socialLinks = useMemo(() => {
    const wa = String(settings?.community_whatsapp_url || '').trim();
    const tg = String(settings?.community_telegram_url || '').trim();
    const yt = String(settings?.community_youtube_url || '').trim();
    return [
      wa ? { key: 'wa', href: wa, label: 'WhatsApp', Icon: WhatsAppIcon, className: 'text-emerald-500 hover:bg-emerald-500/10' } : null,
      tg ? { key: 'tg', href: tg, label: 'Telegram', Icon: TelegramIcon, className: 'text-sky-500 hover:bg-sky-500/10' } : null,
      yt ? { key: 'yt', href: yt, label: 'YouTube', Icon: YouTubeIcon, className: 'text-red-500 hover:bg-red-500/10' } : null,
    ].filter(Boolean);
  }, [settings]);

  const handleToggleTheme = useCallback(() => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setPreference(next);
    persistUserAppearance({ brand_theme: next });
  }, [theme, setPreference, persistUserAppearance]);

  const handleSelectAccent = useCallback(async (key) => {
    setAccent(key);
    await persistUserAppearance({ brand_accent: key });
  }, [setAccent, persistUserAppearance]);

  const toggleFullscreen = async () => {
    // Keep this in the selection gesture; browsers require user activation.
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch {
      toast.error(t('header.fullscreenUnavailable'));
    }
  };

  const handleLogout = async () => {
    try {
      if (onLogout) await onLogout();
      else await logout();
    } finally {
      clearSessionStarted();
      navigate('/login');
    }
  };

  const handleSessionAutoLogout = useCallback(async () => {
    try {
      if (onLogout) await onLogout();
      else await logout();
    } finally {
      clearSessionStarted();
      navigate('/login', { replace: true, state: { reason: 'session_timeout' } });
    }
  }, [onLogout, logout, navigate]);

  const sessionTimer = useOfficeSessionTimer({ onAutoLogout: handleSessionAutoLogout });

  const sessionTimerProps = {
    active: sessionTimer.active,
    elapsedLabel: sessionTimer.elapsedLabel,
    limitMinutes: sessionTimer.limitMinutes,
    remainingLabel: sessionTimer.remainingLabel,
    nearEnd: sessionTimer.nearEnd,
    setAutoLogoutMinutes: sessionTimer.setAutoLogoutMinutes,
    resetClock: sessionTimer.resetClock,
  };

  return (
    <>
      <header className="office-mobile-header office-unified-header sticky top-0 z-30 shrink-0">

        {/* One responsive header; navigation and appearance stay in their existing menus. */}
        <div className="office-mobile-bar office-unified-bar flex items-center">
          {!desktopSidebar && !bottomNavigation && <button
            type="button"
            onClick={onMenu}
            className="office-mobile-menu-btn office-header-control office-header-tone--blue flex h-9 w-9 shrink-0 items-center justify-center rounded-[0.35rem] border border-border/70 bg-card text-foreground transition-all active:scale-95"
            aria-label={menuLabel}
            title={menuLabel}
            aria-expanded={menuExpanded}
            aria-controls="office-main-sidebar"
          >
            <Menu className="h-[17px] w-[17px]" strokeWidth={2} aria-hidden="true" />
          </button>}

          <div className="office-page-slot min-w-0 flex-1 px-0.5">{pageIdentity}</div>
          {modernVisual && <button type="button" className="office-header-search" onClick={onOpenCommandPalette} aria-label="Buscar página ou ação"><Search size={16} aria-hidden="true" /><span>Buscar no seu espaço…</span><kbd>Ctrl K</kbd></button>}

          <div className="office-compact-actions flex shrink-0 items-center gap-1">
            {modernVisual && <IconBtn className="office-modern-search office-header-control" onClick={onOpenCommandPalette} title="Buscar no Office" data-testid="header-modern-search"><Search size={18} /></IconBtn>}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  title={t('header.create')}
                  aria-label={t('header.create')}
                  data-testid="header-mobile-create"
                  data-keep-round={modernVisual || undefined}
                  className="office-mobile-create inline-flex h-9 items-center justify-center gap-1 rounded-[0.35rem] bg-primary px-2.5 text-primary-foreground shadow-sm transition-colors active:scale-95"
                >
                  <Plus className="office-create-icon h-4 w-4 shrink-0" strokeWidth={2} aria-hidden="true" />
                  <span className="text-[12px] font-semibold tracking-tight">{t('header.create')}</span>
                  <ChevronDown className="office-create-chevron h-3 w-3 opacity-80" strokeWidth={2} aria-hidden="true" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8} className="office-header-dropdown office-header-create-menu" onCloseAutoFocus={event => afterMenuClose(event, createPending)}>
                <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('header.createMenu')}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {showQuickTest ? (
                  <DropdownMenuItem
                    className="gap-2.5 py-2.5"
                    onSelect={() => { createPending.current = fx2910?.criar_novo_2910 ? () => navigate('/gestor-xui/tests?novo=1') : () => window.dispatchEvent(new Event('aurax:open-quick-test')); }}
                    data-testid="header-mobile-create-test"
                  >
                    <HeaderMenuIcon icon={Zap} tone="amber" />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold">
                        Criar teste
                      </span>
                      <span className="block text-[11px] text-muted-foreground">
                        {t('header.createTestHint')}
                      </span>
                    </span>
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem
                  className="gap-2.5 py-2.5"
                  onSelect={() => { createPending.current = fx2910?.criar_novo_2910 ? () => navigate('/gestor-xui/clients?novo=1') : () => window.dispatchEvent(new Event('aurax:open-create-client')); }}
                  data-testid="header-mobile-create-client"
                  disabled={!canOperate(user, 'clients.create')}
                >
                  <HeaderMenuIcon icon={UserPlus} tone="blue" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">Criar cliente</span>
                    <span className="block text-[11px] text-muted-foreground">
                      {t('header.createClientHint')}
                    </span>
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem className="gap-2.5 py-2.5" disabled={!canOperate(user, 'resellers.create') || !hasPage('/gestor-xui/resellers')} onSelect={() => { createPending.current = () => navigate('/gestor-xui/resellers?create=1'); }} data-testid="header-create-reseller"><HeaderMenuIcon icon={Users} tone="violet" /><span><strong className="block text-sm">Criar revenda</strong><small className="text-muted-foreground">Abrir cadastro de revenda</small></span></DropdownMenuItem>
                {hidePath2810('/reunioes', fx2910) ? null : <DropdownMenuItem className="gap-2.5 py-2.5" disabled={!hasPage('/reunioes')} onSelect={() => { createPending.current = () => navigate('/reunioes?create=1'); }} data-testid="header-create-meeting"><HeaderMenuIcon icon={CalendarPlus} tone="green" /><span><strong className="block text-sm">Criar reunião</strong><small className="text-muted-foreground">Preparar sala e convite</small></span></DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="office-header-utilities flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild><IconBtn className="office-header-control office-header-tone--violet" title={t('header.shortcuts')} data-testid="header-shortcuts"><MoreHorizontal size={18} /></IconBtn></DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8} className="office-header-dropdown" onCloseAutoFocus={event => afterMenuClose(event, shortcutPending)}>
                <DropdownMenuLabel className="office-header-menu-label">{t('header.navigation')}</DropdownMenuLabel>
                <DropdownMenuItem data-testid="header-shortcut-search" className="gap-2 py-2.5" onSelect={() => { shortcutPending.current = onOpenCommandPalette; }}><HeaderMenuIcon icon={Search} tone="blue" /> Buscar página</DropdownMenuItem>
                {modernVisual && !desktopSidebar && <DropdownMenuItem data-testid="header-shortcut-menu" className="gap-2 py-2.5" onSelect={() => { shortcutPending.current = onMenu; }}><HeaderMenuIcon icon={Menu} tone="blue" /> Menu completo</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="office-header-menu-label">{t('header.workspace')}</DropdownMenuLabel>
                <DropdownMenuItem data-testid="header-shortcut-theme" className="office-header-compact-item gap-2 py-2.5" onSelect={handleToggleTheme}>
                  <HeaderMenuIcon icon={theme === 'dark' ? Sun : Moon} tone="amber" /> {t(theme === 'dark' ? 'header.lightMode' : 'header.darkMode')}
                </DropdownMenuItem>
                {fullscreenAvailable && <DropdownMenuItem data-testid="header-shortcut-fullscreen" className="gap-2 py-2.5" onSelect={toggleFullscreen}><HeaderMenuIcon icon={fullscreen ? Minimize : Maximize} tone="cyan" /> {t(fullscreen ? 'header.exitFullscreen' : 'header.fullscreen')}</DropdownMenuItem>}
                {!isStandalonePwa() && <DropdownMenuItem data-testid="header-shortcut-install" className="gap-2 py-2.5" onSelect={() => { shortcutPending.current = () => openPwaInstallPrompt(); }}><HeaderMenuIcon icon={Download} tone="green" /> Instalar aplicativo</DropdownMenuItem>}
                {socialLinks.length > 0 && <><DropdownMenuSeparator /><DropdownMenuLabel>Comunidade</DropdownMenuLabel>{socialLinks.map(({ key, href, label, Icon }) => <DropdownMenuItem key={key} asChild><a href={href} target="_blank" rel="noopener noreferrer" className="gap-2 py-2.5"><HeaderMenuIcon icon={Icon} tone={key === 'wa' ? 'green' : key === 'yt' ? 'rose' : 'cyan'} /> {label}</a></DropdownMenuItem>)}</>}
              </DropdownMenuContent>
            </DropdownMenu>
            <HeaderNotifyBell
              unreadCount={unreadCount}
              className="office-header-notify office-header-control office-header-tone--amber"
              iconClassName="h-4 w-4"
            />

            <IconBtn
              onClick={handleToggleTheme}
              active={theme === 'dark'}
              title={t('header.toggleTheme')}
              aria-pressed={theme === 'dark'}
              data-testid="header-theme-toggle-mobile"
              className={cn('office-header-theme-toggle office-header-control', theme === 'dark' ? 'office-header-tone--amber' : 'office-header-tone--indigo')}
            >
              {theme === 'dark' ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </IconBtn>
            {modernVisual && <IconBtn className="office-modern-appearance office-header-control" onClick={() => openHeaderDialog(setMobileSheetOpen, 'header-modern-appearance')} title="Personalizar aparência" data-testid="header-modern-appearance"><Palette size={18} /></IconBtn>}

            </div>

            <SaldoHeader2810 accountSummary={accountSummary} displayName={displayName} />{/* CREDITOS_HEADER_2810 */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-keep-round
                  className="office-header-account-trigger relative flex shrink-0 items-center justify-center rounded-md transition-colors"
                  title={[displayName, accountIdentity].filter(Boolean).join(' · ')}
                  aria-label={t('header.myAccount')}
                  data-testid="header-user-menu-mobile"
                >
                  <UserAvatar user={user} size="sm" className="office-header-account-avatar shrink-0" />
                  <span className="office-header-account-copy">
                    <span className="office-header-account-name">{displayName}</span>
                    {accountIdentity ? <span className="office-header-account-detail">{accountIdentity}</span> : null}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8} className="office-header-dropdown office-header-account-menu" onCloseAutoFocus={event => afterMenuClose(event, accountPending)}>
                <DropdownMenuLabel>
                  <span className="office-header-menu-identity block">{displayName}</span>
                  {accountIdentity ? <span className="mt-1 block break-words text-xs font-normal text-muted-foreground">{accountIdentity}</span> : null}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="gap-2 py-2.5" onSelect={() => { accountPending.current = () => openHeaderDialog(setMobileSheetOpen, 'header-user-menu-mobile'); }} data-testid="header-account-preferences">
                  <HeaderMenuIcon icon={Palette} tone="violet" />
                  {t('header.appearanceSession')}
                </DropdownMenuItem>
                <DropdownMenuItem className="gap-2" data-testid="header-account-profile" onSelect={() => { accountPending.current = () => openHeaderDialog(setPerfilOpen, 'header-user-menu-mobile'); }}>
                  <HeaderMenuIcon icon={UserCog} tone="blue" />
                  {t('header.editProfile')}
                </DropdownMenuItem>
                <DropdownMenuItem className="gap-2" data-testid="header-account-password" onSelect={() => { accountPending.current = () => openHeaderDialog(setConfigOpen, 'header-user-menu-mobile'); }}>
                  <HeaderMenuIcon icon={KeyRound} tone="cyan" />
                  {t('account.changePassword')}
                </DropdownMenuItem>
                <DropdownMenuItem className="gap-2" data-testid="header-account-settings" onSelect={() => { accountPending.current = () => navigate('/settings'); }}>
                  <HeaderMenuIcon icon={Settings} tone="cyan" />
                  {t('header.settingsSecurity')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="gap-2 text-destructive focus:text-destructive" data-testid="header-account-logout" onSelect={() => { accountPending.current = handleLogout; }}>
                  <HeaderMenuIcon icon={LogOut} tone="rose" />
                  {t('header.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

      </header>

      <OfficeMobileHeaderSheet
        open={mobileSheetOpen}
        onOpenChange={open => changeHeaderDialog(setMobileSheetOpen, open)}
        accent={accent}
        onSelectAccent={handleSelectAccent}
        sessionTimerProps={sessionTimerProps}
      />

      <ProfileModal open={perfilOpen} onOpenChange={open => changeHeaderDialog(setPerfilOpen, open)} />
      <ConfiguracoesModal open={configOpen} onOpenChange={open => changeHeaderDialog(setConfigOpen, open)} />
    </>
  );
}
